// ============================================================================
// SIZE-ASSIST-01 — the Sizing Assist card on the proposal builder.
//
// County parcel record (parcel_fact via the parcel_lookup RPC — address
// normalization happens in Postgres, the same function the importer used) +
// the house's own equipment history (mirror_equipment) → a suggested A/C
// tonnage and furnace input, with confidence and plain-English reasons.
//
// The suggestion gets LOGGED (pegasus_sizing_estimate, one row per quote) so
// the drift report can later compare suggested vs load-calc'd vs sold.
// The load calc stays the close gate — the card says so on every render.
// ============================================================================
import { useEffect, useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { Ruler, ShieldAlert } from "lucide-react";
import { InfoTip } from "@/components/InfoTip";
import { supabase } from "@/lib/supabase";
import { Card } from "@/components/ui/Card";
import { cn } from "@/lib/cn";
import { classifyEquipment } from "@/lib/equipment";
import { useCustomerEquipment } from "@/features/customer-360/CustomerEquipment";
import {
  buildSizing, decodeEquipSize,
  type ParcelFacts, type ExistingSizes, type SizingSuggestion,
} from "@/lib/sizing";
import type { Quote } from "./quotes";

export type { SizingSuggestion } from "@/lib/sizing";
export { familyHint, isRightSize } from "@/lib/sizing";

interface ParcelHit extends ParcelFacts {
  pin: string | null;
  address_norm: string;
}

/** Quote → service address → county parcel record (0, 1, or several hits). */
function useParcelForQuote(custno: string | null, locno: string | null) {
  return useQuery({
    queryKey: ["parcel_for_quote", custno, locno],
    enabled: !!custno,
    staleTime: 10 * 60 * 1000,
    queryFn: async (): Promise<{ facts: ParcelHit | null; matches: number }> => {
      let q = supabase.from("mirror_location").select("locno, add1, city, zip").eq("custno", custno!).limit(10);
      if (locno) q = q.eq("locno", locno);
      const { data: locs, error } = await q;
      if (error) throw error;
      const loc = (locs ?? [])[0] as { add1: string | null; city: string | null; zip: string | null } | undefined;
      if (!loc?.add1) return { facts: null, matches: 0 };
      const { data: hits, error: e2 } = await supabase.rpc("parcel_lookup", {
        p_address: loc.add1, p_zip: loc.zip ?? null, p_city: loc.city ?? null,
      });
      if (e2) throw e2;
      const rows = (hits ?? []) as ParcelHit[];
      // Prefer the record that actually has building facts.
      const best = rows.find((r) => r.fin_sqft != null) ?? rows[0] ?? null;
      return { facts: best, matches: rows.length };
    },
  });
}

/** Decode the sizes already in the house (active units only — replaced/dupes excluded upstream of us is overkill; newest per family wins here). */
function useExistingSizes(custno: string | null, locno: string | null): { sizes: ExistingSizes; ready: boolean } {
  const eq = useCustomerEquipment(custno ?? undefined, locno ?? null);
  const sizes = useMemo<ExistingSizes>(() => {
    const out: ExistingSizes = { coolingTons: null, coolingModel: null, heatInputKbtu: null, heatModel: null };
    const rows = [...(eq.data ?? [])].sort((a, b) => (b.install_date ?? "").localeCompare(a.install_date ?? ""));
    for (const r of rows) {
      const cat = classifyEquipment(r).category;
      if (out.coolingTons == null && (cat === "ac" || cat === "heat-pump" || cat === "mini-split" || cat === "rooftop" || cat === "magic-pak")) {
        const d = decodeEquipSize(r.model, "cooling");
        if (d?.tons != null) { out.coolingTons = d.tons; out.coolingModel = r.model; }
      }
      if (out.heatInputKbtu == null && (cat === "furnace" || cat === "boiler")) {
        const d = decodeEquipSize(r.model, "heating");
        if (d?.inputKbtu != null) { out.heatInputKbtu = d.inputKbtu; out.heatModel = r.model; }
      }
      if (out.coolingTons != null && out.heatInputKbtu != null) break;
    }
    return out;
  }, [eq.data]);
  return { sizes, ready: !eq.isLoading };
}

export function useSizingAssist(quote: Quote | null | undefined): {
  suggestion: SizingSuggestion | null;
  parcel: ParcelHit | null;
  matches: number;
  ready: boolean;
} {
  const custno = quote?.custno ?? null;
  const locno = quote?.locno ?? null;
  const parcel = useParcelForQuote(custno, locno);
  const { sizes, ready: eqReady } = useExistingSizes(custno, locno);
  const facts = parcel.data?.facts ?? null;
  const suggestion = useMemo(
    () => (parcel.isSuccess && eqReady ? buildSizing(facts, sizes) : null),
    [parcel.isSuccess, eqReady, facts, sizes],
  );
  return { suggestion, parcel: facts, matches: parcel.data?.matches ?? 0, ready: parcel.isSuccess && eqReady };
}

/** Log the suggestion once per distinct payload — the drift report's raw material. */
function useLogEstimate(quote: Quote, suggestion: SizingSuggestion | null, active: boolean) {
  const last = useRef<string>("");
  useEffect(() => {
    if (!active || !suggestion) return;
    const payload = {
      quote_id: quote.id,
      custno: quote.custno,
      locno: quote.locno,
      inputs: suggestion.inputs,
      suggested: {
        cooling_tons: suggestion.coolingTons,
        heat_input_kbtu: suggestion.heatInputKbtu,
        reasons: suggestion.reasons,
        flags: suggestion.flags,
      },
      confidence: suggestion.confidence,
      updated_at: new Date().toISOString(),
    };
    const key = JSON.stringify([payload.inputs, payload.suggested, payload.confidence]);
    if (key === last.current) return;
    last.current = key;
    void (async () => {
      const { data: u } = await supabase.auth.getUser();
      await supabase.from("pegasus_sizing_estimate").upsert(
        { ...payload, created_by: u?.user?.email ?? null },
        { onConflict: "quote_id" },
      );
    })();
  }, [active, suggestion, quote.id, quote.custno, quote.locno]);
}

const fmtTons = (t: number | null) => (t == null ? null : `${t % 1 === 0 ? t : t.toFixed(1)} ton`);
const fmtKbtu = (k: number | null) => (k == null ? null : `${k}k BTU`);

/**
 * The card. Install quotes only; renders nothing when there's nothing to say
 * (no parcel match AND no decodable existing unit) — never an empty panel.
 */
export function SizingCard({ quote, locked }: { quote: Quote; locked: boolean }) {
  const { suggestion, parcel, matches, ready } = useSizingAssist(quote);
  useLogEstimate(quote, suggestion, ready && !locked && !quote.is_practice);
  if (quote.quote_type === "service") return null;
  if (!ready || !suggestion) return null;

  const solid = suggestion.confidence === "solid";
  const Stat = ({ label, value, sub }: { label: string; value: string; sub?: string | null }) => (
    <div className="min-w-0">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</div>
      <div className="truncate text-lg font-bold tabular-nums text-slate-900 [font-family:var(--font-display)]">{value}</div>
      {sub && <div className="truncate text-[11px] text-slate-400">{sub}</div>}
    </div>
  );

  return (
    <Card className="border border-hairline">
      <div className="flex flex-wrap items-center gap-2 border-b border-hairline/70 px-4 py-2">
        <Ruler className="h-4 w-4 text-slate-500" />
        <span className="text-xs font-bold uppercase tracking-wide text-slate-600">Sizing</span>
        <InfoTip label="About sizing">
          County records (finished square feet, year built) and the equipment on record give the likely size, so the proposal
          starts right. {matches > 1 ? `${matches} county records matched this address — the one with building facts is used. ` : ""}
          {suggestion.reasons.join(" ")} {suggestion.cautions.map((c) => c.detail).join(" ")} The load calc is still the close gate — no install sells on this card alone.
        </InfoTip>
        <span
          className={cn(
            "ml-auto rounded-full px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wide",
            solid ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800",
          )}
          title={solid ? "Two independent sources agree" : "Single-source estimate — treat as a starting point"}
        >
          {solid ? "Solid estimate" : "Rough estimate"}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-4 px-4 py-3 sm:grid-cols-4">
        {parcel?.fin_sqft != null && (
          <Stat
            label="The home"
            value={`${parcel.fin_sqft.toLocaleString()} sq ft`}
            sub={[parcel.year_built ? `built ${parcel.year_built}` : null, parcel.city_norm].filter(Boolean).join(" · ") || null}
          />
        )}
        {suggestion.inputs.existingCoolingTons != null && (
          <Stat label="Existing A/C" value={fmtTons(suggestion.inputs.existingCoolingTons)!} />
        )}
        {suggestion.inputs.existingHeatInputKbtu != null && (
          <Stat label="Existing furnace" value={`${suggestion.inputs.existingHeatInputKbtu}k in`} />
        )}
        {suggestion.coolingTons != null && (
          <Stat label="Suggested cooling" value={fmtTons(suggestion.coolingTons)!} />
        )}
        {suggestion.heatInputKbtu != null && (
          <Stat label="Suggested furnace" value={fmtKbtu(suggestion.heatInputKbtu)!} sub="input" />
        )}
      </div>

      {suggestion.cautions.length > 0 && (
        // DESIGN-QUIET-02: a caution is a short chip here; the sentence lives under the (i) and on hover.
        <div className="flex flex-wrap items-center gap-1.5 border-t border-hairline/70 px-4 py-2">
          {suggestion.cautions.map((c, i) => (
            <span key={i} title={c.detail} className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-800 ring-1 ring-amber-200">
              <ShieldAlert className="h-3 w-3" strokeWidth={2} />{c.label}
            </span>
          ))}
        </div>
      )}

    </Card>
  );
}
